import { useCallback } from 'react';

import { useBreakpointValue } from '@chakra-ui/react';

import { LocationType } from '@app/enums';
import { applyPropertyTypeExpansion, mapSearchStateToUrlParams, toNumericIds } from '@app/helpers';
import { calculateZoomFromRadius } from '@app/helpers/map-utils';
import {
    AccommodationItemModel,
    LocationDetailsModel,
    SearchCriteria,
    SearchFilters,
} from '@app/models';
import {
    commonUiStateSlice,
    mapStateSlice,
    setFiltersVisibilityAction,
    setLocationDetailsAction,
    setSelectedAccommodationAction,
    toggleFiltersVisibilityAction,
    updateSearchCriteriaAction,
    updateSearchFiltersAction,
    useAppDispatch,
    useAppSelector,
} from '@app/state';
import {
    fetchMapAccommodations,
    getLocationDetails,
} from '@app/state/api-thunks/map-accommodations.thunk';

/**
 * Hook to access and update map search state
 */
export function useMapSearch() {
    const dispatch = useAppDispatch();
    const searchCriteria = useAppSelector(mapStateSlice.selectors.selectSearchCriteria);
    const filters = useAppSelector(mapStateSlice.selectors.selectSearchFilters);
    const mapAccommodations = useAppSelector(mapStateSlice.selectors.selectMapAccommodations);
    const locationDetails = useAppSelector(mapStateSlice.selectors.selectLocationDetails);
    const isLoadingAccommodations = useAppSelector(
        mapStateSlice.selectors.selectIsLoadingAccommodations,
    );
    const accommodationsError = useAppSelector(mapStateSlice.selectors.selectAccommodationsError);

    const selectedAccommodation = useAppSelector(
        mapStateSlice.selectors.selectSelectedAccommodation,
    );
    const isFiltersContentVisible = useAppSelector(
        mapStateSlice.selectors.selectIsFiltersContentVisible,
    );
    const isMobileBrowser = useAppSelector(commonUiStateSlice.selectors.selectIsMobileVersion);
    const isMobileRes = useBreakpointValue(
        {
            base: true,
            lg: false,
        },
        {
            fallback: isMobileBrowser ? 'base' : 'lg',
        },
    );

    const updateSearchCriteria = useCallback(
        (criteria: Partial<SearchCriteria>, shouldUpdateUrl = false) => {
            dispatch(
                updateSearchCriteriaAction({
                    criteria,
                    shouldUpdateUrl,
                }),
            );
        },
        [],
    );

    const updateSearchFilters = useCallback(
        (filters: Partial<SearchFilters>, shouldUpdateUrl = false) => {
            dispatch(
                updateSearchFiltersAction({
                    filters,
                    shouldUpdateUrl,
                }),
            );
        },
        [],
    );

    const setSelectedAccommodation = useCallback((accommodation: AccommodationItemModel | null) => {
        dispatch(setSelectedAccommodationAction({ accommodation }));
    }, []);

    const setLocationDetails = useCallback((data: LocationDetailsModel) => {
        dispatch(
            setLocationDetailsAction({
                locationDetails: data,
            }),
        );
    }, []);

    const toggleFiltersVisibility = useCallback(() => {
        setTimeout(() => {
            dispatch(toggleFiltersVisibilityAction());
        }, 0);
    }, []);

    const setFiltersVisibility = useCallback((open: boolean) => {
        dispatch(setFiltersVisibilityAction({ open }));
    }, []);

    const getAsUrlParams = useCallback(() => {
        return mapSearchStateToUrlParams(searchCriteria || {}, filters || {});
    }, [searchCriteria, filters]);

    const setMapLocationDetails = useCallback(
        async (geoId: string, locationType: LocationType): Promise<void> => {
            const locationDetails = await getLocationDetails(geoId, locationType);

            if (!locationDetails) {
                return;
            }

            const {
                radius,
                location: { lat },
            } = locationDetails.details;

            const zoom = calculateZoomFromRadius(
                radius,
                window.innerWidth,
                window.innerHeight,
                lat,
            );
            setLocationDetails({
                ...locationDetails,
                details: {
                    ...locationDetails.details,
                    zoom,
                },
            });
        },
        [setLocationDetails],
    );

    const fetchAccommodations = useCallback(() => {
        if (!locationDetails) {
            return;
        }

        const basePropertyTypeIds = toNumericIds(filters.propertyType);
        const expandedPropertyTypeIds = applyPropertyTypeExpansion(basePropertyTypeIds);
        const amenityIds = toNumericIds(filters.amenities);
        const provisionIds = toNumericIds(filters.provision);

        dispatch(
            fetchMapAccommodations({
                checkInDate: searchCriteria.checkInDate || undefined,
                checkOutDate: searchCriteria.checkOutDate || undefined,
                adultsCount: searchCriteria.guests?.adultsCount,
                childrenCount: searchCriteria.guests?.childrenCount,
                childrenAges: searchCriteria.guests?.childrenAges,
                priceFrom: filters.priceFrom ?? undefined,
                priceTo: filters.priceTo ?? undefined,
                locationDetails,
                propertyTypeIds: expandedPropertyTypeIds,
                amenityIds,
                provisionIds,
                reservationFeatures: filters.reservationFeatures,
                rating: filters.rating ?? undefined,
            }),
        );
    }, [searchCriteria, filters, locationDetails]);

    return {
        searchCriteria,
        filters,
        selectedAccommodation,
        isFiltersContentVisible,
        isMobileRes,
        updateSearchCriteria,
        updateSearchFilters,
        setSelectedAccommodation,
        setLocationDetails,
        toggleFiltersVisibility,
        setFiltersVisibility,
        getAsUrlParams,
        mapAccommodations,
        locationDetails,
        isLoadingAccommodations,
        accommodationsError,
        setMapLocationDetails,
        fetchAccommodations,
    };
}
